import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Alert from '../../core/components/Alert.tsx';
import Button from '../../core/components/Button.tsx';
import ConfirmModal from '../../core/components/ConfirmModal.tsx';
import DropArea from '../../core/components/DropArea.tsx';
import DateField from '../../core/components/Fields/DateField.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import TagsField, { Tag } from '../../core/components/Fields/TagsField.tsx';
import FormField from '../../core/components/FormField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import Layout from '../../core/components/Layout.tsx';
import Page from '../../core/components/Page';
import SaveStatus from '../../core/components/SaveStatus.tsx';
import SettingsFormCard, { SettingsCard } from '../../core/components/SettingsFormCard.tsx';
import SettingsNavigation from '../../core/components/SettingsNavigation.tsx';
import TabButton from '../../core/components/TabButton.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import countFieldErrors from '../../core/utils/countFieldErrors.ts';
import { IFilter } from '../../core/utils/filterQuery.ts';
import { url } from '../../core/utils/link.ts';
import useSearchParamState from '../../core/utils/useSearchParamState.tsx';
import { canManageMasterData, hasWorkistPermissions } from '../../users/utils.ts';
import { DataImportConfigView } from '../components/DataImportConfigView.tsx';
import { MasterDataExplorer } from '../components/MasterDataExplorer.tsx';
import { ImportRunsView } from '../components/MasterDataImportLog.tsx';
import SftpImportCard from '../components/SftpImportCard.tsx';
import {
    CONCRETE_LOOKUP_TYPES,
    CONFIG_FIELDS_BY_LOOKUP_TYPE,
    CONFIG_FIELDS_BY_PRODUCT,
    MASTER_DATA_PATH,
} from '../constants.ts';
import {
    ADDRESS_LIST_COLUMNS,
    ARTICLE_LIST_COLUMNS,
    CLIENT_LIST_COLUMNS,
    CONTACT_LIST_COLUMNS,
    CONVERSION_FACTOR_LIST_COLUMNS,
    OFFER_MATCHING_LIST_COLUMNS,
    ORDER_MATCHING_LIST_COLUMNS,
} from '../fileSchemas.ts';
import useLookupDefinitionChannelUsage, {
    LookupDefinitionChannelUsage,
} from '../hooks/useLookupDefinitionChannelUsage.ts';
import {
    DELETE_LOOKUP_DEFINITION,
    DELETE_MAPPINGS_BY_LOOKUP_TYPE,
    GET_LOOKUP_DEFINITIONS,
    GET_PAGINATED_DATA_IMPORT_RUN_SUMMARIES,
    UPDATE_LOOKUP_DEFINITION,
    UPDATE_MASTER_DATA_CONFIG_BY_PRODUCT,
    uploadMasterdataFiles,
} from '../queries.ts';
import { MasterDataImportRun } from '../types.ts';

const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);

const FILE_SCHEMAS_BY_LOOKUP_TYPE: Record<string, { columns: string[]; sampleFileUrl: string }> = {
    ARTICLE_LOOKUP: { columns: ARTICLE_LIST_COLUMNS, sampleFileUrl: '/samples/csv/article_list.csv' },
    CLIENT_LOOKUP: { columns: CLIENT_LIST_COLUMNS, sampleFileUrl: '/samples/csv/client_list.csv' },
    CONTACT_LOOKUP: { columns: CONTACT_LIST_COLUMNS, sampleFileUrl: '/samples/csv/contact_list.csv' },
    ADDRESS_LOOKUP: { columns: ADDRESS_LIST_COLUMNS, sampleFileUrl: '/samples/csv/address_list.csv' },
    CONVERSION_FACTOR_LOOKUP: {
        columns: CONVERSION_FACTOR_LIST_COLUMNS,
        sampleFileUrl: '/samples/csv/conversion_factor_list.csv',
    },
    ORDER_MATCHING_LOOKUP: {
        columns: ORDER_MATCHING_LIST_COLUMNS,
        sampleFileUrl: '/samples/csv/order_matching_list.csv',
    },
    OFFER_MATCHING_LOOKUP: {
        columns: OFFER_MATCHING_LIST_COLUMNS,
        sampleFileUrl: '/samples/csv/offer_matching_list.csv',
    },
};

const MasterDataDetailPage = () => {
    const { lookupDefinitionId } = useParams();
    const { t } = useTranslation(['masterdata', 'config']);
    const { user } = useApplicationContext();
    const { publishToast } = useToaster();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useSearchParamState('tab', 'general');

    const { data } = useQuery(GET_LOOKUP_DEFINITIONS, {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });
    const definition = (data?.lookupDefinitions || []).find((candidate) => candidate.id === lookupDefinitionId);
    const isConcreteType = definition != null && CONCRETE_LOOKUP_TYPES.includes(definition.lookupType);

    const activeVersionFilters: IFilter[] = [
        { name: 'lookup_definition_id', value: [lookupDefinitionId], expression: 'eq' },
        { name: 'is_active_version', value: ['true'], expression: 'eq' },
    ];
    const { data: activeVersionData, refetch: refetchActiveVersion } = useQuery(
        GET_PAGINATED_DATA_IMPORT_RUN_SUMMARIES,
        {
            fetchPolicy: 'cache-and-network',
            variables: { filters: activeVersionFilters, offset: 0, first: 1 },
        }
    );
    const activeVersionRun: MasterDataImportRun | undefined =
        activeVersionData?.dataImportRuns.edges[0]?.node ?? undefined;
    const numEntries = isConcreteType
        ? (activeVersionRun?.ingestionFileNumRows ?? activeVersionRun?.inputFileNumRows)
        : definition?.entryCount;

    // Only used to decide whether the history table has anything to show
    const importRunCountFilters: IFilter[] = [
        { name: 'lookup_definition_id', value: [lookupDefinitionId], expression: 'eq' },
    ];
    const { data: importRunCountData, refetch: refetchImportRunCount } = useQuery(
        GET_PAGINATED_DATA_IMPORT_RUN_SUMMARIES,
        {
            fetchPolicy: 'cache-and-network',
            variables: { filters: importRunCountFilters, offset: 0, first: 1 },
        }
    );
    const hasImportRuns = (importRunCountData?.dataImportRuns.totalCount ?? 0) > 0;

    const apolloClient = useApolloClient();

    const { usageByLookupDefinitionId, channels, refetch: refetchChannelUsage } = useLookupDefinitionChannelUsage();
    const usages = usageByLookupDefinitionId.get(lookupDefinitionId) || [];

    /* assign / unassign channels */

    const canManageUsage = canManageMasterData(user);
    const assignableConfigFields = definition ? CONFIG_FIELDS_BY_LOOKUP_TYPE[definition.lookupType] || [] : [];

    // Callers are responsible for refetching the channel usage once after all their updates
    const updateChannelMasterDataConfig = async (
        channel: LookupDefinitionChannelUsage,
        configField: string,
        value: string
    ) => {
        await apolloClient.mutate({
            mutation: UPDATE_MASTER_DATA_CONFIG_BY_PRODUCT[channel.productKey],
            variables: { channelId: channel.channelId, masterDataConfig: { [configField]: value } },
        });
    };

    const handleUnassign = async (usage: LookupDefinitionChannelUsage, configField: string) => {
        try {
            await updateChannelMasterDataConfig(usage, configField, '');
            publishToast({ description: t('hub.detail.usage.unassignSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('hub.detail.usage.unassignError'), status: 'error' });
        }
    };

    const handleAssign = async (channel: LookupDefinitionChannelUsage, configField: string) => {
        try {
            await updateChannelMasterDataConfig(channel, configField, lookupDefinitionId);
            publishToast({ description: t('hub.detail.usage.assignSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('hub.detail.usage.assignError'), status: 'error' });
        }
    };

    // Products in navigation order that have at least one channel, each with the fields of this
    // lookup type the document type actually uses (plus fields with a leftover assignment, so
    // those stay visible and removable)
    const usageProductGroups = [...new Set(channels.map((channel) => channel.productKey))]
        .map((productKey) => {
            const productFields = CONFIG_FIELDS_BY_PRODUCT[productKey] || [];
            const assignedFields = usages
                .filter((usage) => usage.productKey === productKey)
                .flatMap((usage) => usage.configFields);
            return {
                productKey,
                configFields: assignableConfigFields.filter(
                    (field) => productFields.includes(field) || assignedFields.includes(field)
                ),
            };
        })
        .filter(({ configFields }) => configFields.length > 0);
    const channelNameById = new Map(channels.map((channel) => [channel.channelId, channel.channelName]));

    const assignedChannelIds = (productKey: string, configField: string) =>
        usages
            .filter((usage) => usage.productKey === productKey && usage.configFields.includes(configField))
            .map((usage) => usage.channelId);

    // The definition (other than the current one) a channel field currently points to, if any
    const findAssignedDefinition = (channelId: string, configField: string) => {
        const entry = [...usageByLookupDefinitionId.entries()].find(
            ([candidateId, candidateUsages]) =>
                candidateId !== lookupDefinitionId &&
                candidateUsages.some(
                    (usage) => usage.channelId === channelId && usage.configFields.includes(configField)
                )
        );
        if (!entry) return undefined;
        const definition = (data?.lookupDefinitions || []).find((candidate) => candidate.id === entry[0]);
        return { id: entry[0], name: definition?.name || entry[0] };
    };

    // Adding a channel whose field already points to another definition needs to be confirmed first
    const [pendingAssign, setPendingAssign] = useState<{
        channel: LookupDefinitionChannelUsage;
        configField: string;
        replacedName: string;
    } | null>(null);

    const confirmPendingAssign = async () => {
        if (!pendingAssign) return;
        await handleAssign(pendingAssign.channel, pendingAssign.configField);
        setPendingAssign(null);
        await refetchChannelUsage();
    };

    const handleUsageChange = async (productKey: string, configField: string, nextChannelIds: string[]) => {
        const currentChannelIds = assignedChannelIds(productKey, configField);
        const changes: Promise<void>[] = [];

        for (const channelId of currentChannelIds) {
            if (nextChannelIds.includes(channelId)) continue;
            const channel = channels.find((candidate) => candidate.channelId === channelId);
            if (channel) changes.push(handleUnassign(channel, configField));
        }

        for (const channelId of nextChannelIds) {
            if (currentChannelIds.includes(channelId)) continue;
            // ignore free-text entries that are not channels of this product
            const channel = channels.find(
                (candidate) => candidate.productKey === productKey && candidate.channelId === channelId
            );
            if (!channel) continue;

            const replaced = findAssignedDefinition(channelId, configField);
            if (replaced) {
                setPendingAssign({ channel, configField, replacedName: replaced.name });
            } else {
                changes.push(handleAssign(channel, configField));
            }
        }

        if (changes.length > 0) {
            await Promise.all(changes);
            await refetchChannelUsage();
        }
    };

    /* name form */

    const form = useForm({ defaultValues: { name: '' }, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (definition) form.reset({ name: definition.name || '' });
    }, [definition]);

    const [updateLookupDefinition] = useMutation(UPDATE_LOOKUP_DEFINITION);
    const handleNameSubmit = async (formData) => {
        try {
            // The mutation response updates the cached lookup definition, no refetch needed
            await updateLookupDefinition({ variables: { lookupDefinitionId, name: formData.name } });
            publishToast({ description: t('hub.detail.general.updateSuccess'), status: 'success' });
            form.reset(formData);
        } catch (error) {
            console.error(error);
            publishToast({ description: t('hub.detail.general.updateError'), status: 'error' });
        }
    };

    /* upload */

    const [files, setFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const handleImport = async () => {
        setIsUploading(true);
        try {
            await uploadMasterdataFiles(files, lookupDefinitionId);
            publishToast({ description: t('hub.detail.upload.importSuccess'), status: 'success' });
            setFiles([]);
            await Promise.all([refetchActiveVersion(), refetchImportRunCount()]);
        } catch (error) {
            console.error(error);
            publishToast({ description: t('hub.detail.upload.importError'), status: 'error' });
        } finally {
            setIsUploading(false);
        }
    };

    /* reset mappings (mapping lookups only, mirrors the channel master data config reset) */

    const deleteMappingsMutation = definition ? DELETE_MAPPINGS_BY_LOOKUP_TYPE[definition.lookupType] : undefined;

    const [isResetModalVisible, setResetModalVisible] = useState(false);
    const [resetDate, setResetDate] = useState('');
    const handleResetMappings = async () => {
        try {
            await apolloClient.mutate({
                mutation: deleteMappingsMutation,
                variables: { lookupDefinitionId, beforeDate: resetDate },
            });
            publishToast({ description: t('hub.detail.dangerZone.resetSuccess'), status: 'success' });
            setResetModalVisible(false);
            setResetDate('');
        } catch (error) {
            console.error(error);
            publishToast({ description: t('hub.detail.dangerZone.resetError'), status: 'error' });
        }
    };

    /* delete */

    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const [deleteLookupDefinition] = useMutation(DELETE_LOOKUP_DEFINITION);
    const handleDelete = async () => {
        try {
            const { data: deleteData } = await deleteLookupDefinition({ variables: { lookupDefinitionId } });
            if (!deleteData?.deleteLookupDefinitionMutation?.success) {
                throw new Error('Lookup definition could not be deleted');
            }
            publishToast({ description: t('hub.detail.dangerZone.deleteSuccess'), status: 'success' });
            navigate(url(MASTER_DATA_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('hub.detail.dangerZone.deleteError'), status: 'error' });
        } finally {
            setDeleteModalVisible(false);
        }
    };

    const fileSchema = definition ? FILE_SCHEMAS_BY_LOOKUP_TYPE[definition.lookupType] : undefined;

    const showConfigTab = isConcreteType && canManageMasterData(user);
    // Mapping lookups have neither an import config nor import runs
    const showImportsTab = isConcreteType;
    const effectiveTab =
        (activeTab === 'configuration' && !showConfigTab) || (activeTab === 'imports' && !showImportsTab)
            ? 'general'
            : activeTab;

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1 min-w-0">
                <Page.Header className="flex items-center">
                    <Link
                        className="flex flex-none gap-3 rounded transition-colors hover:bg-surface-strong py-1 px-2 -mx-2 items-center cursor-pointer select-text font-semibold text-base text-content-medium hover:text-content-strong"
                        to={url(MASTER_DATA_PATH)}
                    >
                        {t('hub.overview.title')}
                    </Link>

                    <Page.HeaderSeparator />

                    <Page.HeaderTitle className="min-w-0 truncate">
                        <span title={definition?.name || lookupDefinitionId}>
                            {definition?.name || lookupDefinitionId}
                        </span>
                    </Page.HeaderTitle>

                    {hasWorkistPermissions(user) && definition?.djangoAdminUrl && (
                        <div className="ml-auto flex flex-none gap-2">
                            <Page.HeaderButton asChild>
                                <a href={definition.djangoAdminUrl} target="_blank" rel="noreferrer">
                                    {t('importLog.openInDjangoAdmin')}
                                    <ExternalLinkIcon />
                                </a>
                            </Page.HeaderButton>
                        </div>
                    )}
                </Page.Header>

                <Page.Content lowered className="relative p-0 flex flex-col">
                    <div className="flex w-full flex-none bg-surface-white border-b px-4 border-line-weak items-stretch">
                        <div className="flex flex-1">
                            <TabButton isActive={effectiveTab === 'general'} onClick={() => setActiveTab('general')}>
                                {t('hub.detail.tabs.general')}
                            </TabButton>
                            <TabButton isActive={effectiveTab === 'data'} onClick={() => setActiveTab('data')}>
                                {t('hub.detail.tabs.data')}
                            </TabButton>
                            {showImportsTab && (
                                <TabButton
                                    isActive={effectiveTab === 'imports'}
                                    onClick={() => setActiveTab('imports')}
                                >
                                    {t('hub.detail.tabs.imports')}
                                </TabButton>
                            )}
                            {showConfigTab && (
                                <TabButton
                                    isActive={effectiveTab === 'configuration'}
                                    onClick={() => setActiveTab('configuration')}
                                >
                                    {t('hub.detail.tabs.configuration')}
                                </TabButton>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col gap-6 p-7">
                        {effectiveTab === 'general' && (
                            <>
                                <SettingsFormCard
                                    title={t('hub.detail.general.title')}
                                    onSubmit={form.handleSubmit(handleNameSubmit)}
                                >
                                    <Controller
                                        name="name"
                                        control={form.control}
                                        rules={{ required: t('hub.detail.general.nameRequired') }}
                                        render={({ field }) => (
                                            <FormField
                                                label={t('hub.detail.general.nameLabel')}
                                                required
                                                error={formErrors.name}
                                            >
                                                <StringField required {...field} />
                                            </FormField>
                                        )}
                                    />

                                    <FormField label={t('hub.detail.general.typeLabel')} disabled>
                                        <StringField
                                            disabled
                                            value={definition ? t(`importRun.lookupType.${definition.lookupType}`) : ''}
                                        />
                                    </FormField>

                                    <FormField label={t('hub.detail.general.idLabel')} disabled>
                                        <StringField disabled value={lookupDefinitionId} />
                                    </FormField>

                                    <FormField label={t('hub.detail.general.entriesLabel')} disabled>
                                        <StringField disabled value={numEntries != null ? String(numEntries) : '—'} />
                                    </FormField>

                                    <FormField label={t('hub.detail.general.lastUpdateLabel')} disabled>
                                        <StringField
                                            disabled
                                            value={
                                                activeVersionRun?.finishedAt
                                                    ? format(new Date(activeVersionRun.finishedAt), 'PPpp')
                                                    : '—'
                                            }
                                        />
                                    </FormField>

                                    <SaveStatus
                                        isDirty={form.formState.isDirty}
                                        isSubmitting={form.formState.isSubmitting}
                                        errorCount={countFieldErrors(formErrors)}
                                    />
                                </SettingsFormCard>

                                <SettingsCard
                                    title={t('hub.detail.usage.title')}
                                    description={t('hub.detail.usage.description')}
                                >
                                    {canManageUsage && usageProductGroups.length > 0 ? (
                                        usageProductGroups.map(({ productKey, configFields }) => (
                                            <div key={productKey} className="flex flex-col gap-3">
                                                <h3 className="text-sm font-semibold text-content-strong">
                                                    {t(`hub.products.${productKey}`)}
                                                </h3>

                                                {configFields.map((configField) => {
                                                    const assigned = assignedChannelIds(productKey, configField);
                                                    return (
                                                        <React.Fragment key={configField}>
                                                            <FormField
                                                                label={t(
                                                                    `config:configPage.masterData.form.${configField}`
                                                                )}
                                                            >
                                                                <TagsField
                                                                    value={assigned}
                                                                    onValueChange={(nextChannelIds) =>
                                                                        handleUsageChange(
                                                                            productKey,
                                                                            configField,
                                                                            nextChannelIds
                                                                        )
                                                                    }
                                                                    options={channels
                                                                        .filter(
                                                                            (channel) =>
                                                                                channel.productKey === productKey &&
                                                                                !assigned.includes(channel.channelId)
                                                                        )
                                                                        .map((channel) => ({
                                                                            value: channel.channelId,
                                                                            label: channel.channelName,
                                                                        }))}
                                                                    placeholder={t(
                                                                        'hub.detail.usage.channelsPlaceholder'
                                                                    )}
                                                                    renderTag={({ children, ...tagProps }) => (
                                                                        // The tag value is the channel id; show its name
                                                                        <Tag {...tagProps}>
                                                                            {channelNameById.get(children as string) ||
                                                                                children}
                                                                        </Tag>
                                                                    )}
                                                                />
                                                            </FormField>

                                                            {pendingAssign &&
                                                                pendingAssign.configField === configField &&
                                                                pendingAssign.channel.productKey === productKey && (
                                                                    <Alert severity="warning">
                                                                        <div className="flex flex-col gap-2">
                                                                            <span>
                                                                                {t('hub.detail.usage.replaceWarning', {
                                                                                    channel:
                                                                                        pendingAssign.channel
                                                                                            .channelName,
                                                                                    name: pendingAssign.replacedName,
                                                                                })}
                                                                            </span>
                                                                            <div className="flex justify-end gap-2">
                                                                                <Button
                                                                                    type="button"
                                                                                    onClick={() =>
                                                                                        setPendingAssign(null)
                                                                                    }
                                                                                >
                                                                                    {t(
                                                                                        'hub.detail.usage.replaceCancelButton'
                                                                                    )}
                                                                                </Button>
                                                                                <Button
                                                                                    type="button"
                                                                                    variant="primary"
                                                                                    onClick={confirmPendingAssign}
                                                                                >
                                                                                    {t(
                                                                                        'hub.detail.usage.replaceConfirmButton'
                                                                                    )}
                                                                                </Button>
                                                                            </div>
                                                                        </div>
                                                                    </Alert>
                                                                )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </div>
                                        ))
                                    ) : usages.length > 0 ? (
                                        <ul className="flex flex-col gap-3 text-sm">
                                            {usages.map((usage) => (
                                                <li key={`${usage.productKey}-${usage.channelId}`}>
                                                    <Link
                                                        className="text-content-brand-default hover:text-content-brand-hover"
                                                        to={url(usage.channelPath, { channelId: usage.channelId })}
                                                    >
                                                        {usage.channelName}
                                                    </Link>{' '}
                                                    <span className="text-content-medium">
                                                        ({t(`hub.products.${usage.productKey}`)})
                                                    </span>
                                                    {usage.configFields.length > 0 && (
                                                        <div className="mt-1 flex flex-wrap gap-1">
                                                            {usage.configFields.map((field) => (
                                                                <span
                                                                    key={field}
                                                                    className="inline-flex items-center gap-1 rounded-full bg-surface-strong px-2 py-0.5 text-xs text-content-medium"
                                                                >
                                                                    {t(`config:configPage.masterData.form.${field}`)}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="text-sm text-content-medium">
                                            {t('hub.detail.usage.noUsage')}
                                        </div>
                                    )}
                                </SettingsCard>

                                <SettingsCard
                                    title={
                                        <span className="text-content-danger-strong">
                                            {t('hub.detail.dangerZone.title')}
                                        </span>
                                    }
                                    description={t('hub.detail.dangerZone.description')}
                                    className="border-line-danger-strong"
                                >
                                    {usages.length > 0 && (
                                        <div className="text-sm text-content-medium">
                                            {t('hub.detail.dangerZone.usedByNote', { count: usages.length })}
                                        </div>
                                    )}

                                    {deleteMappingsMutation && (
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="text-sm text-content-medium">
                                                {t('hub.detail.dangerZone.resetDescription')}
                                            </div>
                                            <Button
                                                type="button"
                                                onClick={() => setResetModalVisible(true)}
                                                className="flex-none text-content-danger-strong hover:bg-surface-danger-weak hover:text-content-danger-strong"
                                            >
                                                {t('hub.detail.dangerZone.resetButton')}
                                            </Button>
                                        </div>
                                    )}

                                    <div className="flex justify-end">
                                        <Button
                                            type="button"
                                            variant="primary"
                                            onClick={() => setDeleteModalVisible(true)}
                                            disabled={usages.length > 0}
                                            className="bg-surface-danger-strong-default hover:enabled:!bg-surface-danger-strong-hover active:enabled:!bg-surface-danger-strong-active focus:enabled:!bg-surface-danger-strong-focus"
                                        >
                                            {t('hub.detail.dangerZone.deleteButton')}
                                        </Button>
                                    </div>
                                </SettingsCard>
                            </>
                        )}

                        {effectiveTab === 'configuration' && (
                            <DataImportConfigView
                                lookupDefinitionId={lookupDefinitionId}
                                lookupType={definition?.lookupType}
                                showRawJsonToggle={hasWorkistPermissions(user)}
                            />
                        )}

                        {effectiveTab === 'data' && definition && (
                            <div className="flex flex-col gap-6">
                                <h2 className="text-xl font-semibold text-content-strong">
                                    {t(isConcreteType ? 'hub.detail.explorer.title' : 'hub.detail.mappings.title')}
                                </h2>
                                <MasterDataExplorer
                                    lookupType={definition.lookupType}
                                    lookupDefinitionId={lookupDefinitionId}
                                    className="max-h-[calc(100dvh-16rem)]"
                                />
                            </div>
                        )}

                        {effectiveTab === 'imports' && (
                            <>
                                {hasImportRuns && (
                                    <div className="flex flex-col gap-6">
                                        <h2 className="text-xl font-semibold text-content-strong">
                                            {t('hub.detail.history.title')}
                                        </h2>
                                        <ImportRunsView
                                            baseLookupDefinitionIds={[lookupDefinitionId]}
                                            hasExplorerLink={false}
                                            hasConfigActions={false}
                                        />
                                    </div>
                                )}

                                <SftpImportCard
                                    lookupDefinitionId={lookupDefinitionId}
                                    onImportStarted={() =>
                                        Promise.all([refetchActiveVersion(), refetchImportRunCount()])
                                    }
                                />

                                <SettingsCard
                                    title={t('hub.detail.upload.title')}
                                    description={t('hub.detail.upload.description')}
                                >
                                    <div className="text-sm text-content-medium flex flex-col gap-4">
                                        <DropArea
                                            placeholder={t('hub.detail.upload.filesPlaceholder')}
                                            accept={{ 'text/csv': ['.csv'] }}
                                            onFilesChange={setFiles}
                                            files={files}
                                            maxFiles={1}
                                        />

                                        {fileSchema && (
                                            <>
                                                <p>{t('hub.detail.upload.csvNote')}</p>
                                                <p className="font-mono">{fileSchema.columns.join(', ')}</p>
                                                <p>
                                                    <a target="_blank" href={fileSchema.sampleFileUrl}>
                                                        {t('hub.detail.upload.downloadSampleFile')}
                                                    </a>
                                                </p>
                                            </>
                                        )}
                                    </div>

                                    <div className="flex justify-end">
                                        <Button
                                            type="button"
                                            variant="primary"
                                            onClick={handleImport}
                                            disabled={files.length === 0}
                                            loading={isUploading}
                                        >
                                            {t('hub.detail.upload.importButton')}
                                        </Button>
                                    </div>
                                </SettingsCard>
                            </>
                        )}
                    </div>
                </Page.Content>
            </Page>

            {isResetModalVisible && deleteMappingsMutation && (
                <ConfirmModal
                    onConfirm={handleResetMappings}
                    onCancel={() => {
                        setResetModalVisible(false);
                        setResetDate('');
                    }}
                    title={t('hub.detail.dangerZone.resetModal.title')}
                    danger
                    confirmDisabled={!resetDate}
                >
                    <div className="flex flex-col gap-3">
                        <p>{t('hub.detail.dangerZone.resetModal.description')}</p>
                        <DateField
                            placeholder={t('hub.detail.dangerZone.resetModal.datePlaceholder')}
                            value={resetDate}
                            onValueChange={setResetDate}
                        />
                    </div>
                </ConfirmModal>
            )}

            {isDeleteModalVisible && (
                <ConfirmModal
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteModalVisible(false)}
                    title={t('hub.detail.dangerZone.confirmTitle')}
                    labelConfirm={t('hub.detail.dangerZone.confirmButton')}
                    danger
                >
                    <div className="flex flex-col gap-3">
                        <span>
                            {t('hub.detail.dangerZone.confirmMessage', {
                                name: definition?.name || lookupDefinitionId,
                            })}
                        </span>

                        {usages.length > 0 && (
                            <>
                                <span>{t('hub.detail.dangerZone.usedByWarning')}</span>
                                <ul className="list-disc pl-5">
                                    {usages.map((usage) => (
                                        <li key={`${usage.productKey}-${usage.channelId}`}>
                                            {usage.channelName} ({t(`hub.products.${usage.productKey}`)})
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                </ConfirmModal>
            )}
        </Layout>
    );
};

export default MasterDataDetailPage;
